// GOAT: cache-warm the gallery images of the OTHER colour swatches on the PDP.
//
// Why: unlike the live theme (which renders every colour's images hidden in the DOM and
// swaps with a class toggle), Horizon re-fetches the product page on each swatch change and
// replaces <media-gallery> wholesale — so the newly selected colour's images are cold-cache
// by construction and the user watches them download. This module pre-fetches them so the
// swap lands on a warm cache:
//   - on idle: the first two images of every other colour (skipped for data-saver users)
//   - on swatch hover / touch / focus: that colour's full image set (hover precedes the
//     click by enough to hide the fetch)
//
// The colour → srcset map is emitted by snippets/product-media-gallery-content.liquid as a
// <script type="application/json" data-gallery-color-preload> sibling of <media-gallery>,
// deliberately OUTSIDE the node media-gallery.js replaces, so it survives variant changes.
// Warming uses an Image() with the SAME srcset/sizes the future <img> will carry, so the
// browser resolves the identical candidate URL and the fetch is a true cache hit.

// Must mirror the Liquid normalization in product-media-gallery-content.liquid
// (`handleize`, then strip hyphens/underscores).
const norm = (value) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const warmed = new Set();

// GOAT (perf): how many other colours the idle pass warms. The swatch row shows a handful before
// it overflows, and hover/touch still warms any colour on intent — so an uncapped pass on a
// 24-colour product just queued ~46 full-size downloads nobody asked for.
const IDLE_COLOR_LIMIT = 8;

function warmEntry(srcset, sizes) {
  if (!srcset || warmed.has(srcset)) return;
  warmed.add(srcset);
  const img = new Image();
  // Must be set BEFORE `srcset`: assigning srcset is what starts the fetch, so a priority hint
  // applied afterwards is ignored. Keeps these speculative warms behind the images on screen.
  img.fetchPriority = 'low';
  if (sizes) img.sizes = sizes;
  img.srcset = srcset;
}

function wireMap(node) {
  // The quick-add dialog reuses PDP markup; preloading there would fight the drawer's own
  // fetches for bandwidth.
  if (node.closest('dialog, quick-add-dialog, quick-add-component')) return;

  let data;
  try {
    data = JSON.parse(node.textContent);
  } catch {
    return;
  }

  const colors = data.colors || {};
  const sizes = data.sizes || '';
  const keys = Object.keys(colors);
  if (keys.length === 0) return;

  if (!navigator.connection?.saveData) {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const warmIdle = () =>
      idle(() =>
        keys
          .slice(0, IDLE_COLOR_LIMIT)
          .forEach((key) => colors[key].slice(0, 2).forEach((srcset) => warmEntry(srcset, sizes)))
      );

    // Deferred to `load`, not DOMContentLoaded: requestIdleCallback can fire while the page is
    // still fetching, so these speculative warms used to contend with the gallery's own images —
    // including the LCP one. The hover/touch/focus path below is untouched, and that is the one
    // that actually hides the click latency.
    if (document.readyState === 'complete') warmIdle();
    else window.addEventListener('load', warmIdle, { once: true });
  }

  const warmForTarget = (target) => {
    const label = target instanceof Element ? target.closest('label') : null;
    const input = label?.querySelector('input[data-option-name]');
    if (!input || !/colou?r/i.test(input.dataset.optionName || '')) return;
    const set = colors[norm(input.value)];
    if (set) set.forEach((srcset) => warmEntry(srcset, sizes));
  };

  // Listeners live on the section, which survives the variant-picker's own re-renders.
  const scope = node.closest('.shopify-section') || document;
  scope.addEventListener('pointerover', (event) => warmForTarget(event.target));
  scope.addEventListener('focusin', (event) => warmForTarget(event.target));
  scope.addEventListener('touchstart', (event) => warmForTarget(event.target), { passive: true });
}

function init() {
  document.querySelectorAll('script[data-gallery-color-preload]').forEach(wireMap);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
